import React, { useCallback, useState } from 'react';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Dialog,
    Grid,
    withTheme
} from '@material-ui/core';
import { Formik, Form } from 'formik';
import FormikInput from '../atom/FormikInput';
import { useNotify } from '../../hooks/useNotify';
import { doSwaggerCall } from '../../hooks/useSwaggerClient';
import { CheckIcon, ErrorIcon } from '../../icons';
import { CAPABILITIES } from '../../capabilities/capabilities';

function SignInModal({ setLoggedInUser, showModal, theme, MODAL_TYPE }) {
    const { notifySuccess, notifyError } = useNotify();
    const [emailSent, setEmailSent] = useState(false);

    const handlePwreset = useCallback(
        email => {
            const req = doSwaggerCall('auth', 'resetPasswordRequest', {
                data: {
                    email
                }
            });
            req.then(() => {
                setEmailSent(true);
                notifySuccess('Password reset email sent, please check your inbox.');
            }).catch(error => {
                notifyError(
                    error?.response?.body?.message ||
                        'Failed to send password reset email, please try again.'
                );
                setEmailSent(false);
            });
        },
        [notifySuccess, notifyError]
    );

    return (
        <Dialog
            aria-labelledby="sign-in-modal"
            maxWidth="xs"
            open
            onClose={() => showModal(MODAL_TYPE.NONE)}
        >
            <DialogTitle>Sign-in</DialogTitle>
            <Formik
                initialValues={{ email: '', password: '' }}
                onSubmit={({ email, password }, { setSubmitting, setErrors }) => {
                    doSwaggerCall('auth', 'login', { userData: { email, password } })
                        .then(data => {
                            if (!data.user) {
                                setErrors({ api: data.message });
                                return;
                            }
                            setLoggedInUser(data.user);
                            // Close modal immediately for better UX
                            showModal(MODAL_TYPE.NONE);
                            // Show dashboard alerts for agents if there are alerts in last 24h
                            // Check in background to avoid blocking the login flow
                            // Note: using data.user.capabilities directly instead of useCapability hook, because the hook cannot be called inside callbacks
                            if (
                                data.user.role === 'agent' &&
                                data.user.capabilities?.includes(CAPABILITIES.SHOW_DASHBOARD_ALERTS)
                            ) {
                                const lastShown = localStorage.getItem('dashboardAlertsLastShown');
                                const today = new Date().toISOString().split('T')[0];

                                if (lastShown !== today) {
                                    // Fetch dashboard alerts in background after a brief delay
                                    setTimeout(() => {
                                        doSwaggerCall('users/statistics', 'getDashboardAlerts', {})
                                            .then(alertsData => {
                                                const hasAlerts =
                                                    (alertsData?.customBookings?.count24h || 0) >
                                                        0 ||
                                                    (alertsData?.failedEmails?.count24h || 0) > 0 ||
                                                    alertsData?.totalExpiredUnconfirmedBookings > 0;

                                                if (hasAlerts) {
                                                    showModal(MODAL_TYPE.DASHBOARD_ALERTS);
                                                }
                                            })
                                            .catch(() => {
                                                // Silent fail - don't disrupt user experience
                                            });
                                    }, 200);
                                }
                            }
                        })
                        .catch(error => {
                            setErrors({
                                api:
                                    error?.response?.body?.message ||
                                    'Sign-in failed, please try again.'
                            });
                        })
                        .finally(() => setSubmitting(false));
                }}
            >
                {({ isSubmitting, values, errors, isValid, dirty }) => (
                    <Form>
                        <DialogContent>
                            <Grid container spacing={2}>
                                {errors?.api && !emailSent && (
                                    <Grid
                                        item
                                        container
                                        spacing={2}
                                        xs={12}
                                        style={{
                                            color: theme.palette.error.main
                                        }}
                                        alignItems="center"
                                    >
                                        <Grid item>
                                            <ErrorIcon color="error" />
                                        </Grid>
                                        <Grid item>
                                            <span>{errors?.api}</span>
                                            {errors?.api.indexOf('Incorrect email or password') !==
                                            -1 ? (
                                                <div>
                                                    <br />
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => {
                                                            handlePwreset(values.email);
                                                        }}
                                                    >
                                                        Request password reset email
                                                    </Button>
                                                </div>
                                            ) : null}
                                        </Grid>
                                    </Grid>
                                )}
                                {emailSent && (
                                    <Grid item container spacing={2} xs={12} alignItems="center">
                                        <Grid item>
                                            <CheckIcon style={{ color: '#1e4620' }} />
                                        </Grid>
                                        <Grid item>Password reset sent.</Grid>
                                    </Grid>
                                )}
                                <FormikInput
                                    sm={12}
                                    label="Email"
                                    required
                                    type="email"
                                    name="email"
                                />
                                <FormikInput
                                    sm={12}
                                    label="Password"
                                    required
                                    type="password"
                                    name="password"
                                />
                                <Grid item xs={12}>
                                    <div>
                                        Do you not have an account already?&nbsp;
                                        <a
                                            href="#"
                                            onClick={e => {
                                                e.preventDefault();
                                                showModal(MODAL_TYPE.SIGNUP);
                                            }}
                                        >
                                            Sign up
                                        </a>
                                    </div>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button variant="contained" onClick={() => showModal(MODAL_TYPE.NONE)}>
                                Close
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={isSubmitting || !(isValid && dirty)}
                            >
                                Sign in
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
}

export default withTheme(SignInModal);
